import { useState, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import type { BlogTag } from '../../../types';

interface TagManagerProps {
  tags: BlogTag[];
  onCreateTag: (name: string) => Promise<void>;
  onDeleteTag: (id: string) => Promise<void>;
}

export function TagManager({ tags, onCreateTag, onDeleteTag }: TagManagerProps) {
  const [newName, setNewName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleCreate = useCallback(async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setIsSaving(true);
    try {
      await onCreateTag(trimmed);
      setNewName('');
    } finally {
      setIsSaving(false);
    }
  }, [newName, onCreateTag]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await onDeleteTag(id);
    } finally {
      setDeletingId(null);
    }
  }, [onDeleteTag]);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">Tags</h3>

      {/* Add new tag */}
      <div className="flex gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nova tag..."
          className="h-8 text-sm max-w-xs"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
          disabled={isSaving}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleCreate}
          disabled={isSaving || !newName.trim()}
        >
          <Plus className="w-4 h-4 mr-1" />
          Adicionar
        </Button>
      </div>

      {/* Tag list */}
      {tags.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma tag cadastrada.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge
              key={tag.id}
              variant="secondary"
              className="flex items-center gap-1 pl-3 pr-2 py-1 text-sm"
            >
              {tag.name}
              <button
                type="button"
                className="ml-1 hover:text-destructive transition-colors disabled:opacity-50"
                onClick={() => handleDelete(tag.id)}
                disabled={deletingId === tag.id}
                aria-label={`Remover tag ${tag.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
